import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  InputGroup,
  Badge,
  Card,
} from "react-bootstrap";
import { getCasesByPatientId, createCase } from "../../acases";
import { getPeopleForUser, createPerson } from "../../apeople";

/* ========== Helpers ========== */
const personIdOf = (p) => p?._id?.$oid || p?._id || p?.id || "";

const normalize = (s) =>
  String(s || "")
    .trim()
    .toLowerCase();

const findPersonForCase = (caseObj, peopleArr) => {
  const bhf = normalize(caseObj?.referringDoctorBHF);
  const hpcsa = normalize(caseObj?.referringDoctorHPCSA);
  const name = normalize(caseObj?.referringDoctorName);

  let match =
    peopleArr.find((p) => normalize(p?.hpcsaNumber) === hpcsa && hpcsa) ||
    peopleArr.find((p) => normalize(p?.bhfNumber) === bhf && bhf);

  if (match) return match;

  return peopleArr.find((p) => {
    const full = normalize(
      `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`
    );
    return full === name;
  });
};

// Normalize various _id shapes
const normId = (c) => String(c?._id?.$oid || c?._id || c?.id || "");

// Keep dates as strings "MM-DD-YYYY" (only convert around the date input)
const mmddyyyyToYyyyMmDd = (s) => {
  if (!s) return "";
  const [mm, dd, yyyy] = String(s).split("-");
  if (!mm || !dd || !yyyy) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
};
const yyyyMmDdToMmDdYyyy = (s) => {
  if (!s) return "";
  const [yyyy, mm, dd] = String(s).split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${mm.padStart(2, "0")}-${dd.padStart(2, "0")}-${yyyy}`;
};

// For inbound values that could be ISO or {$date}
export const normalizeToMMDDYYYY = (v) => {
  if (!v) return "";
  if (typeof v === "string") {
    if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v; // already MM-DD-YYYY
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO YYYY-MM-DD...
    if (m) return `${m[2]}-${m[3]}-${m[1]}`;
  }
  if (v && typeof v === "object" && v.$date) {
    const iso = String(v.$date);
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}-${m[3]}-${m[1]}`;
  }
  return "";
};

// Label for the existing cases dropdown
const caseOptionLabel = (c) => {
  const desc = (c?.caseDescription || "").trim() || "Untitled Case";
  const s = normalizeToMMDDYYYY(c?.caseStartDate);
  const e = normalizeToMMDDYYYY(c?.caseEndDate);
  const range = s || e ? `${s || "—"}${e ? ` → ${e}` : ""}` : "";
  return range ? `${desc} • ${range}` : desc;
};

// (invoice) icdSelections -> codes
const codeOf = (obj) => obj?.ICD10_Code || obj?.ICD10_3_Code || "";
const icdFromSelections = (selections = []) => {
  const primaries = selections.filter((s) => s.isPrimary).map((s) => s.item);
  const secondaries = selections.filter((s) => !s.isPrimary).map((s) => s.item);
  const icd101 = primaries[0] ? codeOf(primaries[0]) : "";
  const icd102 = secondaries.map(codeOf).filter(Boolean);
  return { icd101, icd102 };
};

// Lean case object back to parent (for invoice, etc.)
export const mapCaseForEmit = (c) => ({
  _id: normId(c),
  caseDescription: c.caseDescription || "",
  referringDoctorName: c.referringDoctorName || "",
  referringDoctorBHF: c.referringDoctorBHF || "",
  referringDoctorHPCSA: c.referringDoctorHPCSA || "",
  authorizedBy: c.authorizedBy || "",
  authorizationNumber: c.authorizationNumber || "",
  caseStartDate: normalizeToMMDDYYYY(c.caseStartDate),
  caseEndDate: normalizeToMMDDYYYY(c.caseEndDate),
  icd101: c.icd101 || "",
  icd102: Array.isArray(c.icd102) ? c.icd102 : [],
  icd101Full: c.icd101Full || null,
  icd102Full: Array.isArray(c.icd102Full) ? c.icd102Full : [],
  icdSelections: Array.isArray(c.icdSelections) ? c.icdSelections : undefined,
});

/* ========== Component ========== */

export default function CasePickerModal({
  show,
  onHide,
  patientId,
  currentUserId,
  existingReferrers = [], // [{id, name, bhf, hpcsa}, ...]
  invoiceICDSelections = [], // [{item, isPrimary}, ...]
  onConfirm, // (pickedCaseObj) => void
}) {
  // Existing cases
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [mode, setMode] = useState("existing"); // "existing" | "new"

  // New case (inline) fields
  const [people, setPeople] = useState([]);

  // existing referrer mode
  const [useExistingReferrer, setUseExistingReferrer] = useState(true);
  const [selectedReferrerId, setSelectedReferrerId] = useState("");

  // derived display fields
  const [refName, setRefName] = useState("");
  const [refBHF, setRefBHF] = useState("");
  const [refHPCSA, setRefHPCSA] = useState("");

  // new referrer fields (structured like yesterday)
  const [refType, setRefType] = useState("GP");
  const [refTitle, setRefTitle] = useState("");
  const [refFirstName, setRefFirstName] = useState("");
  const [refLastName, setRefLastName] = useState("");

  const [caseDescription, setCaseDescription] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [authorizationNumber, setAuthorizationNumber] = useState("");
  const [caseStartInput, setCaseStartInput] = useState(""); // YYYY-MM-DD for input
  const [caseEndInput, setCaseEndInput] = useState(""); // YYYY-MM-DD for input
  const [caseMaxSessions, setCaseMaxSessions] = useState("");

  // ICD codes (taken from invoice selections, not editing here)
  const { icd101, icd102 } = useMemo(
    () => icdFromSelections(invoiceICDSelections),
    [invoiceICDSelections]
  );

  // Selected existing case object
  const selectedCase = useMemo(
    () => cases.find((c) => normId(c) === selectedCaseId) || null,
    [cases, selectedCaseId]
  );

  useEffect(() => {
    let mounted = true;

    async function loadPeople() {
      try {
        if (!show) return;
        const token = sessionStorage.getItem("user");
        if (!token) return;

        const data = await getPeopleForUser(token);
        const arr = Array.isArray(data) ? data : [];
        if (mounted) {
          setPeople(arr);

          // set default selected referrer if empty
          if (!selectedReferrerId && arr[0]) {
            setSelectedReferrerId(personIdOf(arr[0]));
          }
        }
      } catch (e) {
        console.error("❌ Failed to load people:", e);
        if (mounted) setPeople([]);
      }
    }

    loadPeople();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!selectedCase) return;

    const matched = findPersonForCase(selectedCase, people);
    if (matched) {
      setUseExistingReferrer(true);
      setSelectedReferrerId(personIdOf(matched));
    }
  }, [selectedCase, people]);

  // Load existing cases when opening or patient changes
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!show || !patientId) return;
      setLoading(true);
      setErr("");
      try {
        const data = await getCasesByPatientId(patientId);
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.cases)
          ? data.cases
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.items)
          ? data.items
          : [];
        // sort by start desc (best-effort)
        const sorted = [...arr].sort((a, b) => {
          const aKey = normalizeToMMDDYYYY(a?.caseStartDate);
          const bKey = normalizeToMMDDYYYY(b?.caseStartDate);
          const aS = aKey
            ? `${aKey.slice(6, 10)}${aKey.slice(0, 2)}${aKey.slice(3, 5)}`
            : "";
          const bS = bKey
            ? `${bKey.slice(6, 10)}${bKey.slice(0, 2)}${bKey.slice(3, 5)}`
            : "";
          return bS.localeCompare(aS);
        });
        if (!cancel) {
          setCases(sorted);
          if (sorted[0]) {
            setSelectedCaseId(normId(sorted[0]));
            setMode("existing");
          } else {
            setSelectedCaseId("");
            setMode("new");
          }
        }
      } catch (e) {
        if (!cancel) {
          setErr("Failed to load cases.");
          setCases([]);
          setSelectedCaseId("");
          setMode("new");
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [show, patientId]);

  useEffect(() => {
    if (!useExistingReferrer) return;

    const p = people.find((x) => personIdOf(x) === selectedReferrerId);

    if (p) {
      setRefName(
        `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim()
      );
      setRefBHF(p?.bhfNumber || "");
      setRefHPCSA(p?.hpcsaNumber || "");
    } else {
      setRefName("");
      setRefBHF("");
      setRefHPCSA("");
    }
  }, [useExistingReferrer, selectedReferrerId, people]);

  useEffect(() => {
    if (!useExistingReferrer) {
      setSelectedReferrerId("");
      setRefName("");
      setRefBHF("");
      setRefHPCSA("");
    }
  }, [useExistingReferrer]);

  // Reset new-case form when modal opens or when switching to "new"
  useEffect(() => {
    if (!show) return;
    if (mode !== "new") return;
    const hasPeople = Array.isArray(people) && people.length > 0;
    setUseExistingReferrer(hasPeople);
    setSelectedReferrerId(hasPeople ? personIdOf(people[0]) : "");
    setRefName("");
    setRefBHF("");
    setRefHPCSA("");

    setRefType("GP");
    setRefTitle("");
    setRefFirstName("");
    setRefLastName("");
    setErr("");
    setCaseDescription("");
    setAuthorizedBy("");
    setAuthorizationNumber("");
    setCaseStartInput("");
    setCaseEndInput("");
    setCaseMaxSessions("");
    setUseExistingReferrer(true);
    setSelectedReferrerId(existingReferrers?.[0]?.id || "");
  }, [show, mode, people, existingReferrers]);

  // Emit a picked existing case
  const useSelectedCase = () => {
    setErr("");
    if (!selectedCaseId) {
      setErr("Please select a case or switch to Create New.");
      return;
    }
    const found = cases.find((c) => normId(c) === selectedCaseId);
    if (!found) {
      setErr("Selected case not found.");
      return;
    }
    onConfirm?.(mapCaseForEmit(found));
    onHide?.();
  };

  // Create a new case (saved to DB) then use it
  const createAndUseNewCase = async () => {
    setErr("");
    if (!patientId) return setErr("Patient is required.");
    if (!icd101) {
      return setErr(
        "New case requires at least one primary ICD-10 (taken from the invoice)."
      );
    }

    const token = sessionStorage.getItem("user");
    if (!token) return setErr("You are not logged in. Please sign in again.");

    try {
      let finalRefName = refName || "";
      let finalBHF = refBHF || "";
      let finalHPCSA = refHPCSA || "";

      // ✅ create People first if we're not using an existing referrer
      if (!useExistingReferrer) {
        if (!refFirstName || !refLastName) {
          return setErr("Referrer first name and last name are required.");
        }

        const createdPerson = await createPerson(
          {
            type: refType || "GP",
            title: refTitle || "",
            firstName: refFirstName || "",
            lastName: refLastName || "",
            bhfNumber: finalBHF || "",
            hpcsaNumber: finalHPCSA || "",
          },
          token
        );

        finalRefName = `${createdPerson?.title ?? refTitle ?? ""} ${
          createdPerson?.firstName ?? refFirstName ?? ""
        } ${createdPerson?.lastName ?? refLastName ?? ""}`.trim();

        finalBHF = createdPerson?.bhfNumber ?? finalBHF;
        finalHPCSA = createdPerson?.hpcsaNumber ?? finalHPCSA;

        // optional: refresh local people list so next open has it
        setPeople((prev) => [createdPerson, ...prev].filter(Boolean));
      }

      const payload = {
        patientId,
        referringDoctorName: useExistingReferrer ? refName || "" : finalRefName,
        referringDoctorBHF: finalBHF || "",
        referringDoctorHPCSA: finalHPCSA || "",
        icd101,
        icd102,
        icdSelections: invoiceICDSelections, // keep full shape
        caseDescription: caseDescription || "",
        authorizedBy: authorizedBy || "",
        authorizationNumber: authorizationNumber || "",
        caseStartDate: yyyyMmDdToMmDdYyyy(caseStartInput) || "",
        caseEndDate: yyyyMmDdToMmDdYyyy(caseEndInput) || "",
        caseMaxSessions: caseMaxSessions || "",
        caseCreatedBy: currentUserId || "",
        caseUpdatedBy: currentUserId || "",
      };

      const created = await createCase(payload);

      // hydrate return for emit
      const hydrated = { ...payload, ...created };
      const serverId =
        created?._id?.$oid ||
        created?._id ||
        created?.id ||
        created?.insertedId;
      if (serverId) hydrated._id = serverId;

      onConfirm?.(mapCaseForEmit(hydrated));
      onHide?.();
    } catch (e) {
      console.error("Failed to create case:", e);
      setErr(e?.message || "Failed to create case.");
    }
  };

  const hasExisting = cases.length > 0;

  // Pretty preview helpers
  const PreviewRow = ({ label, value }) => (
    <Row className="mb-1">
      <Col xs={5} md={4} className="text-muted small">
        {label}
      </Col>
      <Col xs={7} md={8}>
        {value || "—"}
      </Col>
    </Row>
  );

  return (
    <Modal show={!!show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Attach Case
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!patientId && (
          <Alert variant="warning" className="mb-3">
            Please select a patient first.
          </Alert>
        )}

        {/* Existing cases chooser + mode toggle */}
        <div className="d-flex gap-2 align-items-end mb-3">
          <div className="flex-grow-1">
            <Form.Label className="fw-bold mb-1">Existing Cases</Form.Label>
            <Form.Select
              size="sm"
              value={selectedCaseId}
              onChange={(e) => {
                setSelectedCaseId(e.target.value);
                setMode("existing");
              }}
              disabled={!patientId || loading || !hasExisting}
              className="rounded-0"
            >
              {loading ? (
                <option value="">Loading…</option>
              ) : !hasExisting ? (
                <option value="">No existing cases for this patient</option>
              ) : (
                <>
                  <option value="">Choose a case…</option>
                  {cases.map((c) => {
                    const id = normId(c);
                    return (
                      <option key={id} value={id}>
                        {caseOptionLabel(c)}
                      </option>
                    );
                  })}
                </>
              )}
            </Form.Select>
            {loading && (
              <Spinner animation="border" size="sm" className="mt-2" />
            )}
          </div>

          <div className="pb-1">
            {mode === "existing" ? (
              <Button
                variant="outline-primary"
                size="sm"
                className="rounded-0"
                onClick={() => {
                  setSelectedCaseId("");
                  setMode("new");
                }}
              >
                + Create New Case
              </Button>
            ) : hasExisting ? (
              <Button
                variant="outline-secondary"
                size="sm"
                className="rounded-0"
                onClick={() => {
                  if (cases[0]) setSelectedCaseId(normId(cases[0]));
                  setMode("existing");
                }}
              >
                ← Back to Existing
              </Button>
            ) : null}
          </div>
        </div>

        {/* Existing case preview */}
        {mode === "existing" && selectedCaseId && selectedCase && (
          <Card className="mb-3">
            <Card.Body>
              <h6 className="text-primary mb-2">
                {selectedCase.caseDescription?.trim() || "Untitled Case"}
              </h6>

              <Row>
                <Col md={7}>
                  <PreviewRow
                    label="Referring Doctor"
                    value={selectedCase.referringDoctorName}
                  />
                  <PreviewRow
                    label="BHF / HPCSA"
                    value={
                      (selectedCase.referringDoctorBHF || "—") +
                      " / " +
                      (selectedCase.referringDoctorHPCSA || "—")
                    }
                  />
                  <PreviewRow
                    label="Authorized By"
                    value={selectedCase.authorizedBy}
                  />
                  <PreviewRow
                    label="Authorization #"
                    value={selectedCase.authorizationNumber}
                  />
                </Col>
                <Col md={5}>
                  <PreviewRow
                    label="Start Date"
                    value={normalizeToMMDDYYYY(selectedCase.caseStartDate)}
                  />
                  <PreviewRow
                    label="End Date"
                    value={normalizeToMMDDYYYY(selectedCase.caseEndDate)}
                  />
                  <PreviewRow
                    label="Max Sessions"
                    value={selectedCase.caseMaxSessions}
                  />
                  <div className="mt-2">
                    <div className="small text-muted mb-1">ICD-10</div>
                    {selectedCase.icd101 && (
                      <Badge bg="secondary" className="me-1">
                        {selectedCase.icd101}
                      </Badge>
                    )}
                    {Array.isArray(selectedCase.icd102) &&
                      selectedCase.icd102.map((code, i) => (
                        <Badge key={i} bg="light" text="dark" className="me-1">
                          {code}
                        </Badge>
                      ))}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* New case form */}
        {mode === "new" && (
          <div className="border rounded p-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Create New Case</strong>
              <Row className="mb-2">
                <Col>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      id="ref-existing"
                      name="referrerMode"
                      label="Use existing referrer"
                      checked={useExistingReferrer === true}
                      onChange={() => setUseExistingReferrer(true)}
                    />
                    <Form.Check
                      type="radio"
                      id="ref-new"
                      name="referrerMode"
                      label="Create new referrer"
                      checked={useExistingReferrer === false}
                      onChange={() => setUseExistingReferrer(false)}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            {useExistingReferrer ? (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>Referring Doctor</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedReferrerId}
                    onChange={(e) => setSelectedReferrerId(e.target.value)}
                  >
                    <option value="">Select referrer...</option>
                    {people.map((p) => {
                      const id = personIdOf(p);
                      const label = `${p?.title ?? ""} ${p?.firstName ?? ""} ${
                        p?.lastName ?? ""
                      }`.trim();
                      const typeLabel = p?.type ? ` (${p.type})` : "";
                      return (
                        <option key={id} value={id}>
                          {label}
                          {typeLabel}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>

                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>BHF</Form.Label>
                      <Form.Control size="sm" value={refBHF} readOnly />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>HPCSA</Form.Label>
                      <Form.Control size="sm" value={refHPCSA} readOnly />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                {/* structured "create new referrer" block */}
                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Type</Form.Label>
                      <Form.Select
                        size="sm"
                        value={refType}
                        onChange={(e) => setRefType(e.target.value)}
                      >
                         <optgroup label="Medical Practitioners">
            <option value="GP">General Practitioner (GP)</option>
            <option value="Cardio">Cardiologist</option>
            <option value="Derm">Dermatologist</option>
            <option value="Endo">Endocrinologist</option>
            <option value="Gastro">Gastroenterologist</option>
            <option value="Neuro">Neurologist</option>
            <option value="Onco">Oncologist</option>
            <option value="Ortho">Orthopaedic Surgeon</option>
            <option value="Pead">Paediatrician</option>
            <option value="Phys">Physician</option>
            <option value="Psych">Psychiatrist</option>
            <option value="Pulmo">Pulmonologist</option>
          </optgroup>

          <optgroup label="Surgical & Diagnostic Specialists">
            <option value="ENT">ENT Specialist</option>
            <option value="Eye">Ophthalmologist</option>
            <option value="Radio">Radiologist</option>
            <option value="Patho">Pathologist</option>
          </optgroup>

          <optgroup label="Allied Health Professionals">
            <option value="Physio">Physiotherapist</option>
            <option value="OT">Occupational Therapist</option>
            <option value="Biokineticist">Biokineticist</option>
            <option value="Dietician">Dietitian</option>
            <option value="Speech">Speech Therapist</option>
            <option value="Audio">Audiologist</option>
            <option value="Psychologist">Clinical Psychologist</option>
            <option value="Chiro">Chiropractor</option>
            <option value="Podiatrist">Podiatrist</option>
          </optgroup>

          <optgroup label="Nursing & Pharmacy">
            <option value="Nurse">Nurse</option>
            <option value="Pharma">Pharmacist</option>
          </optgroup>

          <optgroup label="Dental">
            <option value="Dentist">Dentist</option>
          </optgroup>

          <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Title</Form.Label>
                      <Form.Select
                        size="sm"
                        value={refTitle}
                        onChange={(e) => setRefTitle(e.target.value)}
                      >
                        <option value="">-</option>
                        <option value="Dr">Dr</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Prof">Prof</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        size="sm"
                        value={refFirstName}
                        onChange={(e) => setRefFirstName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        size="sm"
                        value={refLastName}
                        onChange={(e) => setRefLastName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>BHF</Form.Label>
                      <Form.Control
                        size="sm"
                        value={refBHF}
                        onChange={(e) => setRefBHF(e.target.value)}
                        placeholder="e.g. 0123456"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>HPCSA</Form.Label>
                      <Form.Control
                        size="sm"
                        value={refHPCSA}
                        onChange={(e) => setRefHPCSA(e.target.value)}
                        placeholder="e.g. MP0123456"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            <Form.Group className="mb-2">
              <Form.Label>Case Description</Form.Label>
              <Form.Control
                size="sm"
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
                placeholder="Brief description of the case"
              />
            </Form.Group>

            <Row className="mb-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Authorized By</Form.Label>
                  <Form.Control
                    size="sm"
                    value={authorizedBy}
                    onChange={(e) => setAuthorizedBy(e.target.value)}
                    placeholder="Name of authorizer"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Authorization Number</Form.Label>
                  <Form.Control
                    size="sm"
                    value={authorizationNumber}
                    onChange={(e) => setAuthorizationNumber(e.target.value)}
                    placeholder="Auth number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={caseStartInput}
                    onChange={(e) => setCaseStartInput(e.target.value)}
                  />
                  <div className="form-text">
                    Will be saved as{" "}
                    <code>
                      {yyyyMmDdToMmDdYyyy(caseStartInput) || "MM-DD-YYYY"}
                    </code>
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={caseEndInput}
                    onChange={(e) => setCaseEndInput(e.target.value)}
                  />
                  <div className="form-text">
                    Will be saved as{" "}
                    <code>
                      {yyyyMmDdToMmDdYyyy(caseEndInput) || "MM-DD-YYYY"}
                    </code>
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Max Sessions</Form.Label>
                  <InputGroup>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      step={1}
                      value={caseMaxSessions}
                      onChange={(e) => setCaseMaxSessions(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* ICD preview taken from invoice */}
            <div className="mt-2">
              <div className="small text-muted mb-1">ICD-10 (from invoice)</div>
              {icd101 ? (
                <div className="small">
                  <strong>Primary:</strong> {icd101}
                  {icd102?.length ? (
                    <>
                      {"  "} <strong>Secondary:</strong> {icd102.join(", ")}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="small text-danger">
                  No primary ICD-10 selected on the invoice — required to create
                  a case.
                </div>
              )}
            </div>
          </div>
        )}

        {err && <div className="text-danger mt-3">{err}</div>}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex w-100 justify-content-between">
          <div />
          <div className="d-flex gap-2">
            {mode === "existing" && selectedCaseId && (
              <Button
                variant="primary"
                className="rounded-0"
                onClick={useSelectedCase}
                disabled={!patientId || loading}
              >
                Use Selected Case
              </Button>
            )}
            {mode === "new" && (
              <Button
                variant="primary"
                className="rounded-0"
                onClick={createAndUseNewCase}
                disabled={!patientId || loading}
              >
                Create & Use New Case
              </Button>
            )}
            <Button variant="secondary" className="rounded-0" onClick={onHide}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
